import {isToday, isYesterday} from 'date-fns';
import {Tooltip} from 'platform/components';
import {Text, Show, Icon, HStack, ThemeColorPath, ThemeIconKey} from 'platform/foundation';
import {DateTimeFormat, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, parseDate} from 'shared/web';

export interface EventDateProps {
  timestamp: string;
  eventType: 'PLAIN' | 'WHATSAPP';
  messageStatus: 'sent' | 'received' | 'read';
  showReadStatus?: boolean;
}

export function EventDate(props: EventDateProps) {
  const formatDateTime = useDateTimeFormatter();
  const parsedTimestamp = parseDate(props.timestamp);

  const isActivityToday = isToday(parsedTimestamp);
  const isActivityYesterday = isYesterday(parsedTimestamp);

  const messageStatusText = match<EventDateProps['messageStatus'], string>(props.messageStatus)
    .with('sent', always(i18n.t('entity.activityPanel.chat.sent')))
    .with('received', always(i18n.t('entity.activityPanel.chat.received')))
    .with('read', always(i18n.t('entity.activityPanel.chat.read')))
    .exhaustive();

  const messageStatusColor = match<EventDateProps['messageStatus'], ThemeColorPath>(
    props.messageStatus
  )
    .with('sent', always('text.secondary'))
    .with('received', always('text.secondary'))
    .with('read', always('text.link'))
    .exhaustive();

  const messageStatusIcon = match<EventDateProps['messageStatus'], ThemeIconKey>(
    props.messageStatus
  )
    .with('sent', always('action/done'))
    .with('received', always('action/done_all'))
    .with('read', always('action/done_all'))
    .exhaustive();

  return (
    <HStack spacing={1}>
      <Text size="xxSmall" color="secondary">
        {props.eventType === 'PLAIN' && i18n.t('entity.activityPanel.chat.source.chat')}
        {props.eventType === 'WHATSAPP' && i18n.t('entity.activityPanel.chat.source.whatsApp')}
      </Text>

      <Text size="xxSmall" color="secondary">
        {DOT_CHARACTER}
      </Text>

      <Tooltip label={formatDateTime('dateTimeShort', parsedTimestamp)}>
        <Text size="xxSmall" color="secondary" align="right" noWrap>
          <Show when={isActivityToday}>
            {i18n.t('general.labels.today')}, {formatDateTime('timeShort', parsedTimestamp)}
          </Show>

          <Show when={isActivityYesterday}>
            {i18n.t('general.labels.yesterday')}, {formatDateTime('timeShort', parsedTimestamp)}
          </Show>

          <Show when={!isActivityToday && !isActivityYesterday}>
            <DateTimeFormat value={parsedTimestamp} format="dateTimeMedium" />
          </Show>
        </Text>
      </Tooltip>

      <Show when={props.showReadStatus}>
        <Text size="xxSmall" color="secondary">
          {DOT_CHARACTER}
        </Text>

        <Text size="xxSmall" color="secondary">
          <HStack spacing={1}>
            <Icon color={messageStatusColor} value={messageStatusIcon} size={3} />
            {messageStatusText}
          </HStack>
        </Text>
      </Show>
    </HStack>
  );
}
